import type {
    AgentSkillChatEditResponse,
    AgentSkillFlaggedFinding,
    AgentSkillRevision,
} from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { formatTokenDate } from "@helpers/date";
import {
    FLAGGED_FINDING_LABELS,
    KICK_AUTHOR_LABEL,
    PREVIOUS_EDIT_LABEL,
} from "./agentSkillCopy";

export type AgentSkillPanelTab = "chat" | "details" | "versions";

export interface AgentSkillChatItem {
    kind: "system" | "user" | "kick";
    /** Who made the change on a system line; null on chat turns. */
    actor: string | null;
    text: string;
    revisionId: string;
    version: number;
    createdAt: Date;
}

// A revision with no actor was written by Kick: a seeded default or the
// snapshot an override starts from.
export const actorLabel = (revision: AgentSkillRevision) =>
    revision.actor === null
        ? KICK_AUTHOR_LABEL
        : (revision.actor.name ?? revision.actor.email);

// The thread is the revision history read oldest first: a chat revision is a
// user turn and a Kick turn, every other revision is a system line.
export function buildAgentSkillChatThread(
    revisions: AgentSkillRevision[],
): AgentSkillChatItem[] {
    return [...revisions]
        .sort((left, right) => left.version - right.version)
        .flatMap((revision): AgentSkillChatItem[] => {
            const base = {
                actor: null,
                revisionId: revision.id,
                version: revision.version,
                createdAt: revision.createdAt,
            };
            if (revision.chatInstruction !== null) {
                return [
                    {
                        ...base,
                        kind: "user" as const,
                        text: revision.chatInstruction,
                    },
                    {
                        ...base,
                        kind: "kick" as const,
                        text: revision.chatSummary ?? "",
                    },
                ];
            }
            return [
                {
                    ...base,
                    kind: "system" as const,
                    actor: actorLabel(revision),
                    text:
                        revision.version === 1
                            ? "created this skill"
                            : `updated this skill to version ${revision.version}`,
                },
            ];
        });
}

export type RejectedChatEdit = Extract<
    AgentSkillChatEditResponse,
    { outcome: "rejected" }
>;

// A D8 reference error (`unknown_tool:<name>`) shown like a flagged passage,
// so the editor and the chat list both reasons in one shape.
export function consistencyErrorToFinding(
    error: string,
): AgentSkillFlaggedFinding {
    const separator = error.indexOf(":");
    return separator === -1
        ? { code: error, excerpt: "" }
        : {
              code: error.slice(0, separator),
              excerpt: error.slice(separator + 1),
          };
}

const describeFinding = (finding: AgentSkillFlaggedFinding): string =>
    `${FLAGGED_FINDING_LABELS[finding.code] ?? finding.code}: ${finding.excerpt}`;

// One line per reason Kick's draft was not saved, in the order the server
// checked them: unknown tools or skills first, then flagged passages.
export function describeRejectedChatEdit(result: RejectedChatEdit): string[] {
    return [
        ...result.errors.map((error) =>
            describeFinding(consistencyErrorToFinding(error)),
        ),
        ...result.findings.map((finding) =>
            describeFinding({ ...finding, excerpt: `"${finding.excerpt}"` }),
        ),
    ];
}

export interface AgentSkillRevisionGroup<
    T extends GroupableRevision = AgentSkillRevision,
> {
    key: string;
    /** Null for the live skill; the reset heading for an archived override. */
    label: string | null;
    revisions: T[];
}

// Reset dates arrive as Dates when parsed and as ISO strings straight off
// the wire; both compare the same way.
type GroupableRevision = Pick<AgentSkillRevision, "skillId"> & {
    skillArchivedAt: Date | string | null;
};

const resetTime = (value: Date | string | null): number =>
    value === null ? 0 : new Date(value).getTime();

// The live skill's revisions first, then each reset override under its own
// heading, newest reset first (spec 7). Order within a group is the server's.
export function groupAgentSkillRevisions<T extends GroupableRevision>(
    revisions: T[],
): Array<AgentSkillRevisionGroup<T>> {
    const live = revisions.filter(
        (revision) => revision.skillArchivedAt === null,
    );
    const archived = new Map<string, T[]>();
    for (const revision of revisions) {
        if (revision.skillArchivedAt !== null) {
            archived.set(revision.skillId, [
                ...(archived.get(revision.skillId) ?? []),
                revision,
            ]);
        }
    }
    const resetGroups = [...archived.entries()]
        .map(([skillId, group]) => ({
            key: skillId,
            resetAt: group[0].skillArchivedAt,
            revisions: group,
        }))
        .sort(
            (left, right) => resetTime(right.resetAt) - resetTime(left.resetAt),
        )
        .map(({ key, resetAt, revisions: group }) => ({
            key,
            label: `${PREVIOUS_EDIT_LABEL} (reset on ${formatTokenDate(resetAt)})`,
            revisions: group,
        }));
    return [
        ...(live.length === 0
            ? []
            : [{ key: "live", label: null, revisions: live }]),
        ...resetGroups,
    ];
}
